import { ConnectionDetails, convertConnectionToDetails, mergeConnectionDetails as merge, QueryInputType } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DispatchSummaryCallVolumeEnum,
  DispatchSummaryWithCache,
  DispatchSummaryWithCacheSearch,
  SearchDispatchSummaryWithCacheResponseDispatchSummaryWithCacheConnectionArgs,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput } from '@/utils';
import graphApi from '@/api';
import { SearchDispatchSummary } from '@/features/SummaryHistory/api/queries/SearchDispatchSummary';
import { refetchDispatchSummaryMarket, searchDispatchSummaryMarket } from './searchDispatchSummaryMarket';
import { DispatchSummaryWithMarketComments } from '../../components/SummaryHistoryForm/types';
// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  callVolumes?: DispatchSummaryCallVolumeEnum[];
} & {
  [key in keyof DispatchSummaryWithCacheSearch]?: any[];
})[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchSummaryWithCache>>;
}
interface ServiceMethodWithComments {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchSummaryWithMarketComments>>;
}

// SearchDispatchSummary Service
export const { searchDispatchSummary, refetchDispatchSummary, useDispatchSummary } = ((): {
  searchDispatchSummary: ServiceMethod;
  refetchDispatchSummary: ServiceMethod;
  useDispatchSummary: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchDispatchSummary).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchSummaryWithCache>> => {
        const query: DispatchSummaryWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput([item?.startDatetime, item?.endDatetime], QueryInputType.RANGE, SortDirectionEnum.Desc),
          callVolume: queryInput.orNull(item?.callVolumes),
        }));
        const args: SearchDispatchSummaryWithCacheResponseDispatchSummaryWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query,
          },
        };
        const opts = { merge: options?.merge ? 'searchDispatchSummary.dispatchSummaryTableConnection' : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty('searchDispatchSummary.dispatchSummaryTableConnection', res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchDispatchSummary: fetch,
    refetchDispatchSummary: refetch,
    useDispatchSummary: useService,
  };
})();

export const { searchDispatchSummaryWithMarketComments, refetchDispatchSummaryWithMarketComments } = ((): {
  searchDispatchSummaryWithMarketComments: ServiceMethodWithComments;
  refetchDispatchSummaryWithMarketComments: ServiceMethodWithComments;
} => {
  const fetch: ServiceMethodWithComments = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<DispatchSummaryWithMarketComments>> => {
    const dispatchSummaries = await searchDispatchSummary(payload, options);
    const res: ConnectionDetails<DispatchSummaryWithMarketComments> = {
      ...dispatchSummaries,
      rows: dispatchSummaries.rows.map((row) => ({
        ...row,
        marketComments: [],
        marketCommentsForm: {},
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const marketComments = await searchDispatchSummaryMarket([{ dispatchSummaryId: queryInput(allIds) }]);
      marketComments.rows.forEach((comment) => {
        const index = res.rows.findIndex((row) => row.id === comment.dispatchSummaryId);
        if (index !== -1) {
          res.rows[index].marketComments.push(comment);
          res.rows[index].marketCommentsForm[comment.airportCode] = comment.comment;
        }
      });
    }
    return {
      ...res,
    };
  };

  const refetch: ServiceMethodWithComments = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<DispatchSummaryWithMarketComments>> => {
    const dispatchSummaries = await refetchDispatchSummary(payload, options);
    const res: ConnectionDetails<DispatchSummaryWithMarketComments> = {
      ...dispatchSummaries,
      rows: dispatchSummaries.rows.map((row) => ({
        ...row,
        marketComments: [],
        marketCommentsForm: {},
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const marketComments = await refetchDispatchSummaryMarket([{ dispatchSummaryId: queryInput(allIds) }]);
      marketComments.rows.forEach((comment) => {
        const index = res.rows.findIndex((row) => row.id === comment.dispatchSummaryId);
        if (index !== -1) {
          res.rows[index].marketComments.push(comment);
          res.rows[index].marketCommentsForm[comment.airportCode] = comment.comment;
        }
      });
    }
    return {
      ...res,
    };
  };
  return {
    searchDispatchSummaryWithMarketComments: fetch,
    refetchDispatchSummaryWithMarketComments: refetch,
  };
})();
